import { makeStyles } from "tss-react/mui"
import { Box, Stack } from "@mui/material"
import { BillReceiversType } from "../../../../company/admin/accounting/bill-receivers/billReceiversTypes"
import {
    AccountingDetailsCostCodes,
    MeetingServiceOrder,
} from "../../../../merchant/accounting/accounting/accountingTypes"
import { tailwindPalette } from "../../../../theme/tailwindPalette"
import {
    SectionBlock,
    SectionItemData,
    SectionItemLabel,
    SectionItemLink,
    SectionSubTitle,
} from "../../../../../shared/components/dialogs/receiptConfirmOrderDialog/components/common/ReceiptPrimitives"

const useStyles = makeStyles()((theme) => ({
    container: {
        background: tailwindPalette.emerald[50],
        [theme.breakpoints.down("sm")]: {
            background: "white",
            borderRadius: 0,
            paddingTop: 0,
            paddingBottom: "2rem",
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
    },
    wrap: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "1.5rem",
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "1fr",
        },
    },
    gridBox: {
        display: "grid",
        gridTemplateColumns: "9rem 1fr",
        gap: "1.5rem",
    },
}))

type BillReceiverProps = Readonly<{
    billReceiver: MeetingServiceOrder["buyerCustomerParty"]
    costCodes?: AccountingDetailsCostCodes[]
}>

export function BillReceiver({ billReceiver, costCodes }: BillReceiverProps) {
    const { classes } = useStyles()

    const renderContactInfo = () => {
        const label =
            billReceiver?.contactPerson && billReceiver.debitorNo
                ? `${billReceiver?.contactPerson} (ID: ${billReceiver.debitorNo})`
                : billReceiver?.contactPerson

        return (
            <Box pt={2}>
                <SectionItemLabel translationKey="confirm-order-dialog-bill-receiver-contact">
                    Contact:
                </SectionItemLabel>
                {label && <SectionItemData>{label}</SectionItemData>}
                {billReceiver?.email && (
                    <SectionItemLink href={`mailto:${billReceiver.email}`}>{billReceiver.email}</SectionItemLink>
                )}
                {billReceiver?.phoneNumber && <SectionItemData>T: {billReceiver.phoneNumber}</SectionItemData>}
            </Box>
        )
    }

    const renderEndpoint = () => {
        if (billReceiver?.EAN) {
            return <SectionItemData>EAN: {billReceiver?.EAN}</SectionItemData>
        }

        if (billReceiver?.email) {
            return <SectionItemLink href={`mailto:${billReceiver.email}`}>{billReceiver.email}</SectionItemLink>
        }

        return null
    }

    const debitor = (name: string) => {
        return (
            <SectionItemLabel translationKey="confirm-order-dialog-bill-receiver-debitor" translationVars={{ name }}>
                Debitor: {name}
            </SectionItemLabel>
        )
    }

    const renderAddress = () => {
        return (
            <SectionItemData>
                {billReceiver?.streetName} {billReceiver.streetNumber}, {billReceiver.postalCode} {billReceiver.city}
            </SectionItemData>
        )
    }

    const renderByType = () => {
        switch (billReceiver?.type) {
            case BillReceiversType.PRIVATE_COMPANY:
                return (
                    <>
                        {billReceiver?.vatNumber && <SectionItemData>CVR: {billReceiver.vatNumber}</SectionItemData>}
                        {billReceiver.debitorNo && debitor(billReceiver.debitorNo)}
                        {renderEndpoint()}
                        {renderContactInfo()}
                    </>
                )
            case BillReceiversType.PUBLIC_INSTITUTION:
                return (
                    <>
                        {billReceiver.debitorNo && debitor(billReceiver.debitorNo)}
                        {renderEndpoint()}
                        {renderContactInfo()}
                    </>
                )
            case BillReceiversType.PRIVATE_PERSON:
                return (
                    <>
                        {billReceiver.debitorNo && debitor(billReceiver.debitorNo)}
                        {renderEndpoint()}
                        {billReceiver?.phoneNumber && <SectionItemData>T: {billReceiver.phoneNumber}</SectionItemData>}
                    </>
                )
            default:
                return null
        }
    }

    return (
        <SectionBlock
            classNames={classes.container}
            translationKey="confirm-order-dialog-bill-receiver"
            title="Bill receiver"
        >
            <Box className={classes.wrap}>
                <Stack gap="0.5rem">
                    <SectionSubTitle>{billReceiver.name}</SectionSubTitle>
                    {renderAddress()}
                    {renderByType()}
                </Stack>
                {costCodes?.length && (
                    <Stack gap=".5rem">
                        <SectionSubTitle translationKey="confirm-order-dialog-bill-receiver-cost-centers">
                            Cost centers:
                        </SectionSubTitle>
                        {costCodes.map((costCode) => (
                            <Box className={classes.gridBox}>
                                <SectionItemLabel>{costCode.name}</SectionItemLabel>
                                <SectionItemData textAlign="right">
                                    {costCode.label ? `${costCode.value}, ${costCode.label}` : costCode.value}
                                </SectionItemData>
                            </Box>
                        ))}
                    </Stack>
                )}
            </Box>
        </SectionBlock>
    )
}
